import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { User, Code, Shield, Target, Coffee, Quote } from 'lucide-react';

const About = ({ data }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-black via-gray-900 to-black">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center mb-4">
            <div className="h-px bg-gradient-to-r from-transparent via-purple-400 to-transparent flex-1 max-w-xs" />
            <User className="w-8 h-8 text-purple-400 mx-4" />
            <div className="h-px bg-gradient-to-r from-transparent via-purple-400 to-transparent flex-1 max-w-xs" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-purple-400 mb-4">
            About Me
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            The journey of a cybersecurity enthusiast passionate about making the digital world safer.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content Section */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-6"
          >
            {data.content.map((paragraph, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="relative"
              >
                <Card className="bg-gray-800/30 border-gray-700 backdrop-blur-sm hover:border-purple-400/50 transition-all duration-300">
                  <CardContent className="p-6">
                    <p className="text-gray-300 leading-relaxed text-lg">
                      {paragraph}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Visual Elements & Stats */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Animated Background Elements */}
            <div className="relative">
              {/* Central Profile Area */}
              <div className="relative z-10 bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700 rounded-lg p-8 backdrop-blur-sm">
                <div className="text-center mb-8">
                  <motion.div
                    className="w-32 h-32 mx-auto mb-4 relative"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-cyan-400 rounded-full animate-pulse" />
                    <div className="absolute inset-2 bg-gray-900 rounded-full flex items-center justify-center">
                      <Shield className="w-16 h-16 text-purple-400" />
                    </div>
                  </motion.div>
                  
                  <h3 className="text-2xl font-bold text-white mb-2">Cybersecurity Expert</h3>
                  <p className="text-purple-400 font-medium">Ethical Hacker & Security Analyst</p>
                </div>

                {/* Expertise Areas */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {[
                    { icon: Shield, label: 'Penetration Testing', color: 'text-green-400' },
                    { icon: Code, label: 'Security Tools', color: 'text-cyan-400' },
                    { icon: Target, label: 'CTF Competitions', color: 'text-red-400' },
                    { icon: Coffee, label: 'Bug Bounty', color: 'text-yellow-400' }
                  ].map((item, index) => {
                    const IconComponent = item.icon;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 + 0.5, duration: 0.5 }}
                        whileHover={{ scale: 1.05 }}
                        className="text-center p-4 bg-gray-700/30 border border-gray-600 rounded-lg hover:border-purple-400/50 transition-all duration-300"
                      >
                        <IconComponent className={`w-6 h-6 ${item.color} mx-auto mb-2`} />
                        <p className="text-xs text-gray-300 font-medium">{item.label}</p>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Philosophy */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.7, duration: 0.6 }}
                  className="relative"
                >
                  <Card className="bg-gradient-to-br from-purple-900/20 to-cyan-900/20 border-purple-400/30 backdrop-blur-sm">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-3">
                        <Quote className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
                        <div>
                          <p className="text-white font-medium text-lg italic mb-2">
                            {data.funFact}
                          </p>
                          <p className="text-purple-400 text-sm font-semibold">
                            - My Security Philosophy
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-10 -left-10 w-20 h-20 bg-purple-400/10 rounded-full blur-xl animate-pulse" />
              <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-cyan-400/10 rounded-full blur-xl animate-pulse delay-1000" />
            </div>
          </motion.div>
        </div>

        {/* Skills Summary */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16"
        >
          <h3 className="text-2xl font-bold text-center text-white mb-8">Core Competencies</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'Ethical Hacking',
              'Penetration Testing',
              'Digital Forensics',
              'Malware Analysis',
              'Mobile Security',
              'Web Application Security',
              'Network Security',
              'Incident Response',
              'Security Auditing',
              'Risk Assessment',
              'CTF Competitions',
              'Bug Bounty Hunting'
            ].map((skill, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 + 0.6, duration: 0.4 }}
                whileHover={{ scale: 1.05 }}
              >
                <Badge 
                  className="bg-gray-800/50 text-purple-400 border-purple-400/30 hover:bg-purple-400/10 transition-all duration-300 px-4 py-2 text-sm font-medium"
                >
                  {skill}
                </Badge>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;